import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Avatar,
  Button,
  Box,
  Chip,
  Grid,
} from "@mui/material";
import moment from "moment";
import { getDepartmentMedal, getManagementMedal } from "./VoteCard";
import { connect } from "react-redux";
import {
  getVoteDetails,
  getVotingBehaviors,
  getVotingRatings,
} from "../../../store/voting/actions";
import { useEffect, useState } from "react";

const VoteDetailsDialog = ({
  open,
  onClose,
  voteData,
  voteBehaviors,
  voteRatings,
  getVotingBehaviors,
  getVotingRatings,
  getVoteDetails,
}) => {
  const [loadingData, setLoadingData] = useState(true);
  const [ratedBehaviors, setRatedBehaviors] = useState([]);

  useEffect(() => {
    if (voteRatings.length === 0) {
      getVotingRatings(setLoadingData);
    } else setLoadingData(false);

    if (voteBehaviors.length === 0) {
      getVotingBehaviors(setLoadingData);
    } else setLoadingData(false);

    if (!!voteData.voteScoreId) {
      getVoteDetails(voteData.voteScoreId, (status, data) => {
        setLoadingData(status);
        setRatedBehaviors(data);
      });
    } else setLoadingData(false);

    return () => {
      setLoadingData(true);
    };
  }, []);

  if (!voteData) return null;

  const {
    names,
    positionName,
    fiscalYearName,
    departmentNo,
    managementNo,
    voterCount,
    behaviorRate,
    typeId,
    votedOn,
    comment,
    passedOnDepartmentLevel,
    passedOnManagementLevel,
  } = voteData;

  const managementMedal =
    typeId === "M" && !!passedOnManagementLevel
      ? getManagementMedal(managementNo)
      : null;
  const departmentMedal =
    typeId === "D" && !!passedOnDepartmentLevel
      ? getDepartmentMedal(departmentNo)
      : null;

  const isDepartmentPublished = passedOnDepartmentLevel !== null;
  const isManagementPublished =
    !!isDepartmentPublished && passedOnManagementLevel !== null;
  const isPublished = isDepartmentPublished || isManagementPublished;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          borderRadius: "16px",
          padding: "20px",
        },
      }}
    >
      <DialogTitle textAlign="center">
        <Avatar
          sx={{
            bgcolor: "#1976d2",
            width: 70,
            height: 70,
            margin: "0 auto",
            fontSize: "2rem",
          }}
        >
          {names.charAt(0)}
        </Avatar>
        <Typography variant="h5" mt={2}>
          {names}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {positionName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Winner Badge */}
        <Box mb={2} textAlign="center">
          {managementMedal && (
            <>
              <Typography variant="h3" mb={1}>
                {managementMedal.medal}
              </Typography>
              <Chip
                label={managementMedal.label}
                style={{
                  backgroundColor: managementMedal.color,
                  color:
                    managementMedal.color === "#FFD700" ? "#333333" : "white",
                  fontWeight: "bold",
                }}
              />
            </>
          )}
          {departmentMedal && (
            <>
              <Typography variant="h3" mb={1}>
                {departmentMedal.trophy}
              </Typography>
              <Chip
                label={departmentMedal.label}
                style={{
                  backgroundColor: departmentMedal.color,
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            </>
          )}
        </Box>

        <Typography variant="body1" gutterBottom>
          <strong>Fiscal Year:</strong> {fiscalYearName}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>
            {managementMedal ? "Management No" : "Department No"}:
          </strong>{" "}
          {isPublished ? (managementMedal ? managementNo : departmentNo) : "-"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Total Score:</strong> {isPublished ? voterCount : "-"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Total Rate:</strong> {isPublished ? behaviorRate : "-"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Voted On:</strong> {moment(votedOn).format("LL")}
        </Typography>

        {/* Behavior Ratings */}
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>
            Ratings:
          </Typography>
          <Grid container spacing={1}>
            {ratedBehaviors?.map((ratedBehavior, index) => {
              const behavior = voteBehaviors.find(
                (item) => item.id === ratedBehavior.behaviorId
              );
              const rating = voteRatings.find(
                (item) => item.rate === ratedBehavior.rate
              );
              return (
                <Grid item xs={6} key={index}>
                  <Typography variant="body2">
                    <strong>{behavior?.name}:</strong> {rating?.name}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Comment */}
        {comment && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Comment:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {comment}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: "column", gap: 1 }}>
        {managementMedal && (
          <Button
            variant="outlined"
            color="success"
            fullWidth
            href={`/certificates/${voteData.id}.pdf`} // Assume you generate certificates
            target="_blank"
          >
            View Certificate
          </Button>
        )}
        <Button onClick={onClose} variant="contained" fullWidth>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({ env, loading, voteBehaviors, voteRatings }) => {
  return { env, loading, voteBehaviors, voteRatings };
};

export default connect(mapStateToProps, {
  getVotingBehaviors,
  getVotingRatings,
  getVoteDetails,
})(VoteDetailsDialog);
